// src/hooks/useAdminIam.ts
import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSurfaceOrigins } from '../../packages/config/domain';
import { supabase } from '@/integrations/supabase/client';

/**
 * useAdminIam - проверка админских прав через BFF endpoint /api/admin/iam
 *
 * Использует новый каноничный admin infrastructure:
 * - RPC is_admin_v1() с SECURITY DEFINER
 * - Таблица admin_users как source of truth
 */

type AdminSurfaceEntry = {
  hostname: string;
  pathPrefix: string | null;
};

function buildAdminSurfaceEntries(): AdminSurfaceEntry[] {
  return getSurfaceOrigins('admin')
    .map(origin => {
      try {
        const url = new URL(origin);
        const hostname = url.hostname.toLowerCase();
        const normalizedPath = url.pathname.replace(/\/+$/, '');
        const pathPrefix = normalizedPath && normalizedPath !== '/' ? normalizedPath : null;

        return { hostname, pathPrefix } satisfies AdminSurfaceEntry;
      } catch {
        return null;
      }
    })
    .filter((entry): entry is AdminSurfaceEntry => entry !== null);
}

const ADMIN_SURFACE_ENTRIES = buildAdminSurfaceEntries();

const IS_TEST_ENV = process.env.NODE_ENV === 'test';
const SESSION_POLL_ATTEMPTS = 6;
const SESSION_POLL_BASE_DELAY_MS = IS_TEST_ENV ? 10 : 250;
const SESSION_POLL_BACKOFF_FACTOR = IS_TEST_ENV ? 1.1 : 1.3;

async function waitForSupabaseSession(): Promise<Session | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  for (let attempt = 0; attempt < SESSION_POLL_ATTEMPTS; attempt++) {
    try {
      const { data } = await supabase.auth.getSession();
      const session = data.session ?? null;

      if (session?.access_token || session?.user) {
        return session;
      }
    } catch {
      // Ignore transient errors and retry with backoff
    }

    const backoffMs = Math.round(
      SESSION_POLL_BASE_DELAY_MS * Math.pow(SESSION_POLL_BACKOFF_FACTOR, attempt)
    );

    await new Promise(resolve => setTimeout(resolve, backoffMs));
  }

  return null;
}

function isAdminSurfaceEnvironment(): boolean {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname.toLowerCase();
  const pathname = window.location.pathname;

  return ADMIN_SURFACE_ENTRIES.some(entry => {
    if (entry.hostname !== hostname) return false;
    if (!entry.pathPrefix) return true;

    return pathname === entry.pathPrefix || pathname.startsWith(`${entry.pathPrefix}/`);
  });
}

export type AdminIamResult = {
  isAdmin: boolean;
  canPerformAdminActions: boolean;
  isLoading: boolean;
  userId?: string | null;
  email?: string | null;
  failureCount: number;
  lastErrorCode: string | null;
  retry: () => void;
};

type AdminIamPayload = {
  isAdmin?: boolean;
  userId?: string | null;
  email?: string | null;
};

type AdminIamFetchSuccess = {
  ok: true;
  payload: AdminIamPayload;
};

type AdminIamFetchUnauthorized = {
  ok: false;
  code: 'UNAUTHORIZED';
};

export type AdminIamFetchResult = AdminIamFetchSuccess | AdminIamFetchUnauthorized;

export async function fetchAdminIamStatus(
  accessToken: string | null,
  fetchImpl: typeof fetch = fetch
): Promise<AdminIamFetchResult> {
  const headers: Record<string, string> = {
    'Cache-Control': 'no-cache',
    Accept: 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetchImpl('/api/admin/iam', {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers,
  });

  if (response.status === 401 || response.status === 403) {
    return { ok: false, code: 'UNAUTHORIZED' };
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const json = await response.json();

  return {
    ok: true,
    payload: {
      isAdmin: Boolean(json?.isAdmin),
      userId: json?.userId ?? null,
      email: json?.email ?? null,
    },
  };
}

export function useAdminIam(): AdminIamResult {
  const [state, setState] = useState({
    isAdmin: false,
    isLoading: true,
    userId: null as string | null,
    email: null as string | null,
    failureCount: 0,
    lastErrorCode: null as string | null,
  });

  const checkAdminStatus = useCallback(async () => {
    if (!isAdminSurfaceEnvironment()) {
      setState({
        isAdmin: false,
        userId: null,
        email: null,
        failureCount: 0,
        lastErrorCode: null,
        isLoading: false,
      });
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, lastErrorCode: null }));

    let keepLoading = false;

    try {
      const session = await waitForSupabaseSession();

      if (!session) {
        keepLoading = true;
        setState({
          isAdmin: false,
          userId: null,
          email: null,
          failureCount: 0,
          lastErrorCode: 'SESSION_PENDING',
          isLoading: true,
        });
        return;
      }

      const accessToken = session.access_token ?? null;

      const result = await fetchAdminIamStatus(accessToken);

      if (!result.ok) {
        setState({
          isAdmin: false,
          userId: null,
          email: null,
          failureCount: 0,
          lastErrorCode: result.code,
          isLoading: false,
        });
        return;
      }

      const payload = result.payload;

      const finalAdminStatus = Boolean(payload.isAdmin);

      setState({
        isAdmin: finalAdminStatus,
        userId: payload.userId || null,
        email: payload.email || null,
        failureCount: 0,
        lastErrorCode: null,
        isLoading: false,
      });

    } catch (error) {
      let errorCode = 'UNKNOWN';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          errorCode = 'NETWORK';
        } else if (error.message.includes('timeout')) {
          errorCode = 'TIMEOUT';
        } else {
          errorCode = 'SERVER';
        }
      }

      setState(prev => ({
        isAdmin: false,
        userId: null,
        email: null,
        failureCount: prev.failureCount + 1,
        lastErrorCode: errorCode,
        isLoading: keepLoading,
      }));
    } finally {
      if (!keepLoading) {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    }
  }, []);

  const retry = useCallback(() => {
    void checkAdminStatus();
  }, [checkAdminStatus]);

  // Initial check on mount
  useEffect(() => {
    void checkAdminStatus();
  }, [checkAdminStatus]);

  // Resync on tab focus (optional - can be disabled if too aggressive)
  useEffect(() => {
    // 🛡️ CRITICAL FIX: Check window/document availability for SSR safety
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const onFocus = () => {
      // 🛡️ CRITICAL FIX: Double-check document availability in event handler
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        void checkAdminStatus();
      }
    };

    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onFocus);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus);
      }
    };
  }, [checkAdminStatus]);

  return {
    isAdmin: state.isAdmin,
    canPerformAdminActions: state.isAdmin,
    isLoading: state.isLoading,
    userId: state.userId,
    email: state.email,
    failureCount: state.failureCount,
    lastErrorCode: state.lastErrorCode,
    retry,
  };
}